/**
 * Handover — who holds each of this page's tickets now. The buyer's own
 * order token and a recipient's slice token reach the identical surface, so
 * this state is the same state on both: a recipient hands a ticket on again
 * through the very cell the buyer handed it over through.
 *
 * Zero I/O. The mounted app's own order-access.sync.ts is the sole caller of
 * the three callables and the sole writer of every server-resolved field;
 * this file declares the book, the optimistic mutations the sheet performs on
 * it, and the side channels that carry those mutations out. The reconciler
 * owes this book five things and nothing else: `listHandovers` on mount into
 * `handoversLanded`, exactly one of `handoverLanded`/`handoverFailed` per
 * `handTicketsOver$` intent (the sheet holds its busy state until one of them
 * lands), `takeTicketsBack` off `takeTicketsBack$`, a `listHandovers` re-read
 * into `handoversLanded` once each of those writes has settled — refused as
 * readily as accepted — and an empty book when the page leaves, so a token's
 * handovers never survive into the next one.
 *
 * Optimistic by construction: naming a person and tapping a channel is the
 * buyer's own act and cannot fail locally, so the row lands in the book on
 * the tap and the gold marking on their card is already there while the
 * write travels. Only two fields wait for the server — `handoverId`, the
 * name a take-back reclaims by, and `url`, the link the message carries —
 * because both are minted from a token no client can compose.
 *
 * The address a mail handover is sent to is the one thing the sheet gathers
 * that never lands here at all: it rides `handTicketsOver$` out to the
 * callable, which sends the mail and stores it nowhere, and this book — which
 * the cards read and a reload replaces from the server — has no field for it.
 * Sending the same link again therefore asks the buyer for it again.
 *
 * The optimistic shape carries only what the caller's own act settles. The
 * callable also supersedes records this page holds and cascades ones it
 * cannot even see — `ListedHandover` carries no chain edge, so a descendant
 * of a reclaimed record is unreachable from here — which is why every write
 * is followed by a re-read whose answer replaces the book outright. What the
 * page shows between the tap and that answer is the one thing the tap itself
 * makes true: these tickets now belong to this person.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import {
	type HandTicketsOver,
	type ListHandovers,
	TicketType,
} from "@momo/types";
import { ReplaySubject, Subject } from "rxjs";
import {
	type OrderAccessCarouselItem,
	type OrderAccessTicket,
	orderAccessCarousel,
	orderAccessEvent,
	orderAccessState,
} from "./order-access.state.ts";

/**
 * One handover as this page holds it: the three fields the contract's own
 * `ListedHandover` carries — picked from it, never re-declared, so a
 * reshape there is a compile error here — plus the two the callable alone
 * can answer.
 *
 * `draftId` is the client-minted handle the sheet waits on: the row exists
 * from the tap, and the sheet must recognise its own row again once the
 * callable names it. It stays on the row after landing so the book has one
 * stable identity per row whichever half of its fields have resolved.
 */
export type HeldHandover = Pick<
	ListHandovers.ListedHandover,
	"name" | "ticketIds" | "sentVia" | "reclaimable"
> & {
	readonly draftId: string;
	/** Null until the callable names the record — a take-back has nothing to
	 *  reclaim by until then. */
	readonly handoverId: string | null;
	/** Null until the callable mints the slice token this link is composed
	 *  from; the message waits on it, the card's gold marking does not. */
	readonly url: string | null;
};

/** Every live handover of the tickets this page's token carries — one row
 *  per act of giving, a ticket in at most one of them. Replaced wholesale by
 *  `listHandovers` on mount and reset to empty when the page leaves.
 *
 *  keepOnClear, for orderAccessState's reason: the book is the token's own
 *  answer, read once per mount behind order-access.sync.ts's guard, so a
 *  clearStates() landing after that read would empty every caption with
 *  nothing left to re-read it. */
export const handoverBook = state<readonly HeldHandover[]>(
	"storefront/order-access/handovers",
).memory([], { keepOnClear: true });

/** Which handover holds each ticket — the card's own lookup, so a rail of N
 *  cards costs one pass over the book rather than N scans of it. */
export const handoverByTicket = computed<ReadonlyMap<string, HeldHandover>>(
	() =>
		new Map(
			handoverBook.value.flatMap((held) =>
				held.ticketIds.map((ticketId) => [ticketId, held] as const),
			),
		),
);

/** How many of this page's tickets somebody else now holds, and how many the
 *  reader still does. */
export type HandoverTally = {
	readonly given: number;
	readonly yours: number;
};

/** That division, null unless the book is genuinely MIXED — at least one
 *  ticket handed on AND at least one not. A state condition, never a count
 *  threshold: at zero handovers there is nothing to divide, and once every
 *  ticket has been handed on there is nothing left to say, so both ends read
 *  null and an order that never hands a ticket on has nothing to render.
 *
 *  Null a third way, while the order itself is still minting: a whole that is
 *  still growing is not a whole to divide, so a switch standing there would
 *  name a total the rail beside it contradicts on the next render.
 *
 *  Counted over the order's own tickets against the book's ready-made lookup
 *  rather than by a second pass over the book: the lookup is what tells a
 *  ticket nobody holds from one somebody does, and it is already built for
 *  the gold marking every card reads. */
export const handoverTally = computed<HandoverTally | null>(() => {
	const data = orderAccessState.value;
	if (!("tickets" in data)) return null;
	// A ticket still minting stands in the rail as a placeholder no chip can
	// count, so "All 2" beside three rows would be a number the reader can see
	// is wrong. The same settle the readout waits on.
	if (data.status === "paid" && data.fulfillment === "partial") return null;
	const held = handoverByTicket.get();
	const given = data.tickets.reduce(
		(count, ticket) => (held.has(ticket.ticketId) ? count + 1 : count),
		0,
	);
	return given === 0 || given === data.tickets.length
		? null
		: { given, yours: data.tickets.length - given };
});

/** The rail's rows minus every ticket that has been handed on — what the
 *  "still with you" scope shows. A placeholder row stays: a ticket still
 *  being minted has been given to nobody. */
export const carouselStillYours = computed<readonly OrderAccessCarouselItem[]>(
	() => {
		const held = handoverByTicket.get();
		return orderAccessCarousel
			.get()
			.filter(
				(item) => item.kind !== "ticket" || !held.has(item.ticket.ticketId),
			);
	},
);

/** The rail's rows scoped to the tickets somebody else now holds — what the
 *  "given away" scope shows. A placeholder row does NOT stay: a ticket still
 *  being minted has been given to nobody, which is the same reason its twin
 *  above keeps one. */
export const carouselGivenAway = computed<readonly OrderAccessCarouselItem[]>(
	() => {
		const held = handoverByTicket.get();
		return orderAccessCarousel
			.get()
			.filter(
				(item) => item.kind === "ticket" && held.has(item.ticket.ticketId),
			);
	},
);

/** Every name already in the book, first-use order — the one-tap suggestions
 *  for giving a second ticket to the same person. */
export const handoverNames = computed<readonly string[]>(() => [
	...new Set(handoverBook.value.map((held) => held.name)),
]);

/** One row of the sheet's ticket picker: a ticket of this order, the handover
 *  holding it while it is no longer the caller's own, and the venue-calendar
 *  day (`YYYY-MM-DD`) its type admits on — null for a type that spans the
 *  whole event, which the picker bands separately exactly as the event page
 *  does. */
export type HandoverPickerRow = {
	readonly ticket: OrderAccessTicket;
	readonly handover: HeldHandover | null;
	readonly programmeDay: string | null;
};

/**
 * Every ticket this page carries, joined to its holder and to the programme
 * day the sheet groups by — the card rail reads the same join for its gold
 * marking and its caption.
 *
 * The day is derived, never stored: `TicketType.dayKeyOf` is the catalog's own key,
 * so a day named here and a day named on the shop page are one value. A
 * window opening at or before the event and closing at or after it is the
 * whole-event type the event page bands beneath the days, and an absent
 * window reads the same way for the reason the ticket's own field states —
 * no window admits for the event's whole span. An order fulfilled before
 * `entryWindow` reached this snapshot therefore lands every ticket in that
 * one band, which is a single undivided list and no grouping chrome at all.
 */
export const handoverPickerRows = computed<readonly HandoverPickerRow[]>(() => {
	const data = orderAccessState.value;
	const event = orderAccessEvent.get();
	if (!("tickets" in data) || event === null) return [];
	const held = handoverByTicket.get();
	const eventStart = event.schedule.start.toMillis();
	const eventEnd = event.schedule.end.toMillis();
	return data.tickets.map((ticket) => {
		const window = ticket.entryWindow;
		const opensAt = window?.opensAt.toMillis();
		return {
			ticket,
			handover: held.get(ticket.ticketId) ?? null,
			programmeDay:
				window == null ||
				opensAt === undefined ||
				(opensAt <= eventStart && window.closesAt.toMillis() >= eventEnd)
					? null
					: TicketType.dayKeyOf(opensAt, event.timezone),
		};
	});
});

/**
 * Which ticket ids a quantity of one (programme day, type) group resolves to.
 * The buyer names a COUNT, never ids: tickets of one type in an order are
 * interchangeable, and a hash-digest display id carries no decision value.
 *
 * Raising the count appends the group's next still-unpicked ids in the
 * order's own order; lowering it releases the most recent picks first, so the
 * ticket whose control opened the sheet is the last one to leave. A count
 * above what the group actually holds — a typed numeral, or a stale row —
 * clamps to that; anything not a finite number reads as none.
 */
export function resolveGroupPick(
	picked: readonly string[],
	groupTicketIds: readonly string[],
	quantity: number,
): readonly string[] {
	const inGroup = picked.filter((id) => groupTicketIds.includes(id));
	const next = Math.max(
		0,
		Math.min(Number.isFinite(quantity) ? quantity : 0, groupTicketIds.length),
	);
	if (next === inGroup.length) return picked;
	if (next > inGroup.length) {
		const already = new Set(picked);
		return [
			...picked,
			...groupTicketIds
				.filter((id) => !already.has(id))
				.slice(0, next - inGroup.length),
		];
	}
	const released = new Set(inGroup.slice(next - inGroup.length));
	return picked.filter((id) => !released.has(id));
}

/**
 * What the caller asked for, on its way to `handTicketsOver`. The four wire
 * fields are picked from the callable's own input contract; `accessToken` is
 * not among them because the token is the mounted app's to resolve, and
 * `draftId` is this page's own handle for the row the reply lands on.
 *
 * `recipientEmail` is the one field on this type that never reaches the book.
 * It is the address the deployment sends this handover's link to, asked for
 * at the E-Mail exit and carried out on this intent — the wire is its only
 * destination. The optionality is the contract's own: every other channel
 * sends without one.
 */
export type HandoverIntent = Pick<
	HandTicketsOver.HandTicketsOverInput,
	"ticketIds" | "name" | "sentVia" | "recipientEmail"
> & { readonly draftId: string };

/** The give, on its way out — order-access.sync.ts is the one subscriber,
 *  and the callable's reply comes back through `handoverLanded`. */
export const handTicketsOver$ = new Subject<HandoverIntent>();

/** The reclaim, on its way out, carrying the handover id the record is
 *  reclaimed by. */
export const takeTicketsBack$ = new Subject<string>();

/** How one reclaim settled. `withdrawn`: the page stopped waiting before the
 *  answer came — the organization it was asked under is no longer the active
 *  one — so whether the record was taken back is unknown here. */
export const ticketsTakenBack$ = new Subject<{
	readonly handoverId: string;
	readonly outcome: "accepted" | "refused" | "withdrawn";
}>();

/**
 * How one give settled — exactly one per `handTicketsOver$` intent, from the
 * reconciler's own reply, whether that reply landed the record or refused it.
 *
 * The outcome is named on the settlement rather than left to be read back off
 * the book, because the book publishes through a signal that defers every
 * emission by a microtask: a subscriber holding `handoverBook.$` alongside
 * this stream is handed the row as it stood BEFORE the give landed, so it can
 * never see the record it is asking about. Only a consumer reading
 * `handoverBook.value` synchronously sees the landed row, and an event whose
 * own outcome has to be reconstructed from a second cell is a race however it
 * is read.
 *
 * The mail's outcome rides here and not on the book for a separate reason: it
 * is not a fact about who holds a ticket. The record stands and the link is
 * live whether the provider took the mail or refused it, and `ListedHandover`
 * carries no send outcome at all — so a field for it would be blanked by the
 * very re-read every write settles through, one round trip after it appeared.
 * What is owed instead is telling the person who asked for that mail, while
 * they are still looking at the sheet they asked from.
 *
 * A ReplaySubject because the give goes out before its caller starts waiting:
 * a reply that lands first must still reach it.
 */
export type HandoverSettlement =
	| {
			readonly draftId: string;
			readonly outcome: "landed";
			/** What the callable said about the mail this give asked for: true the
			 *  provider took it, false the provider refused it, null no mail was
			 *  attempted at all — WhatsApp, the share sheet and the record-only
			 *  exit, which ask for none. Null is held apart from false because a
			 *  "the mail didn't go" notice fired at a buyer who never asked for one
			 *  is a failure they cannot act on and did not cause. */
			readonly mailAccepted: boolean | null;
	  }
	| {
			readonly draftId: string;
			readonly outcome: "refused";
	  }
	| {
			/** The page stopped waiting before the reply came, so whether the
			 *  record landed is unknown here: the callable may have written it. */
			readonly draftId: string;
			readonly outcome: "withdrawn";
	  };

export const handoverSettled$ = new ReplaySubject<HandoverSettlement>(1);

/**
 * Records a give and sends it. Supersession takes the named tickets out of
 * every record that still holds one, leaving that record with what it still
 * carries and dropping it only once nothing is left — a couple's second
 * ticket stays with the person who was given it when only the first is
 * re-routed. That is the one half of supersession this page can compute; the
 * rest arrives with the re-read.
 *
 * Returns the row's own `draftId` so the caller can wait for the link.
 */
export function handTicketsOver(
	intent: Omit<HandoverIntent, "draftId">,
): string {
	const draftId = crypto.randomUUID();
	const claimed = new Set(intent.ticketIds);
	handoverBook.replace([
		...handoverBook.value
			.map((held) => ({
				...held,
				ticketIds: held.ticketIds.filter((ticketId) => !claimed.has(ticketId)),
			}))
			.filter((held) => held.ticketIds.length > 0),
		// Named field by field, never spread from the intent: the intent also
		// carries `recipientEmail`, and a spread would land a third party's
		// address in the book — read by every card on the page and replaced
		// only by a server answer that has never heard of it.
		{
			draftId,
			handoverId: null,
			url: null,
			name: intent.name,
			ticketIds: intent.ticketIds,
			sentVia: intent.sentVia,
			// This page is the giver, so the row is its own to reclaim from the
			// moment it appears; the server's re-read says the same.
			reclaimable: true,
		},
	]);
	handTicketsOver$.next({ ...intent, draftId });
	return draftId;
}

/** Keep the current holder visible until the authoritative reread answers;
 * a refused write or unreadable reply must leave the take-back retry reachable. */
export function takeTicketsBack(handoverId: string): void {
	takeTicketsBack$.next(handoverId);
}

/** The whole book as `listHandovers` reads it — the mount read, and the
 *  re-read every write settles through. It is the authority: supersession and
 *  the take-back cascade both reach records this page cannot recompute, so
 *  the server's own answer replaces the book rather than merging into it. */
export function handoversLanded(
	handovers: readonly ListHandovers.ListedHandover[],
): void {
	handoverBook.replace([
		...handovers.map(
			({ handoverId, url, name, ticketIds, sentVia, reclaimable }) => ({
				draftId: handoverId,
				handoverId,
				url,
				name,
				ticketIds,
				sentVia,
				reclaimable,
			}),
		),
		// A give still travelling is not yet a fact the server can report, so
		// it outlives the read — otherwise the tickets it names flick back to
		// their previous holder for exactly one round trip.
		...handoverBook.value.filter((held) => held.handoverId === null),
	]);
}

/** `handTicketsOver`'s own reply: the record is named and the link exists, so
 *  the sheet's wait ends and the message can carry it. */
export function handoverLanded(
	draftId: string,
	landed: HandTicketsOver.HandTicketsOverOutput,
): void {
	handoverBook.replace(
		handoverBook.value.map((held) =>
			held.draftId === draftId
				? { ...held, handoverId: landed.handoverId, url: landed.url }
				: held,
		),
	);
	// After the row, never before: a consumer reading `handoverBook.value`
	// synchronously off this settlement has to find the landed row. That
	// ordering reaches the cell alone — `handoverBook.$` publishes a microtask
	// later — which is why the outcome travels on the settlement itself.
	handoverSettled$.next({
		draftId,
		outcome: "landed",
		mailAccepted: landed.mailAccepted ?? null,
	});
}

/** The write never landed — the row goes, which is also how the sheet learns
 *  its wait is over. Rows this give shrank locally are restored by the
 *  reconciler's own re-read, which runs on a refused write exactly as it runs
 *  on a settled one, rather than from a snapshot kept here against a failure
 *  that mostly does not happen. */
export function handoverFailed(draftId: string): void {
	handoverBook.replace(
		handoverBook.value.filter((held) => held.draftId !== draftId),
	);
	handoverSettled$.next({ draftId, outcome: "refused" });
}
